const _excluded = ["value", "onChange", "defaultValue", "disabled", "onInputChange", "getOptionLabel", "filterOptions", "freeSolo", "size", "renderOption", "isOptionEqualToValue", "required"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import * as React from "react";
import { useMultipleSelection } from "downshift";
import { renderMultipleOptionDefault } from "../_internal/MultipleOption";
import { getOptionLabelDefault } from "../_internal/Option";
import { defaultComparator } from "../../helpers/utils/comparator";
import { BaseComboBox } from "./BaseComboBox";
import { filterOptionsDefault, useMultipleSelectionStateReducer } from "./helpers";
export function MultipleComboBox(_ref) {
  let {
      value: valueProp,
      onChange: onChangeProp,
      defaultValue,
      disabled,
      onInputChange,
      getOptionLabel = getOptionLabelDefault,
      filterOptions = filterOptionsDefault,
      freeSolo,
      size,
      renderOption: renderOptionProp = renderMultipleOptionDefault,
      isOptionEqualToValue = defaultComparator,
      required
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  if (valueProp !== undefined && !Array.isArray(valueProp)) {
    throw new Error("MultipleCombobox requires value to be an array.");
  }
  if (defaultValue !== undefined && !Array.isArray(defaultValue)) {
    throw new Error("MultipleCombobox requires defaultValue to be an array.");
  }
  const {
    getDropdownProps,
    selectedItems,
    setSelectedItems
  } = useMultipleSelection({
    stateReducer: useMultipleSelectionStateReducer,
    itemToString: getOptionLabelDefault,
    selectedItems: valueProp,
    initialSelectedItems: defaultValue
  });
  const onChange = React.useCallback(newOption => {
    if (!newOption || Array.isArray(newOption) || disabled) {
      return;
    }
    const itemIndex = selectedItems.findIndex(option => {
      return isOptionEqualToValue?.(option, newOption);
    });
    const newSelectedOptions = itemIndex === -1 ? [...selectedItems, newOption] // add
    : [...selectedItems.slice(0, itemIndex), ...selectedItems.slice(itemIndex + 1)]; // remove

    setSelectedItems(newSelectedOptions);
    if (typeof onChangeProp === "function") {
      onChangeProp(newSelectedOptions);
    }
  }, [setSelectedItems, onChangeProp, selectedItems, isOptionEqualToValue, disabled]);
  const renderOption = React.useCallback(props => renderOptionProp(_objectSpread(_objectSpread({}, props), {}, {
    selected: selectedItems.some(selectedOption => {
      return isOptionEqualToValue(selectedOption, props.option);
    })
  }), renderMultipleOptionDefault), [isOptionEqualToValue, renderOptionProp, selectedItems]);
  const handleFilterOptions = React.useCallback((options, params) => {
    const filtered = filterOptions(options, params);
    if (freeSolo) {
      const customOptionIndex = filtered.findIndex(option => option.isCustom);
      const customOption = filtered[customOptionIndex];
      if (customOptionIndex > -1 && selectedItems.filter(selected => selected.value === customOption.value).length > 0) {
        return [...filtered.slice(0, customOptionIndex), ...filtered.slice(customOptionIndex + 1)];
      }
    }
    return filtered;
  }, [filterOptions, freeSolo, selectedItems]);
  const handleDelete = React.useCallback(indexToDelete => {
    const newSelectedItems = [...selectedItems.slice(0, indexToDelete), ...selectedItems.slice(indexToDelete + 1)];
    setSelectedItems(newSelectedItems);
    if (typeof onChangeProp === "function") {
      onChangeProp(newSelectedItems);
    }
  }, [selectedItems, setSelectedItems, onChangeProp]);
  const handleKeyDown = React.useCallback(function keyDownHandler(event) {
    const {
      value: newInputValue
    } = event.target;
    if (event.key === "Enter" && freeSolo && newInputValue && newInputValue.length && !selectedItems.find(item => item.value === newInputValue)) {
      onChange({
        value: newInputValue
      });
    }
  }, [freeSolo, onChange, selectedItems]);
  const handleGetDropdownProps = React.useCallback(ref => {
    return getDropdownProps({
      ref,
      onKeyDown: handleKeyDown,
      onChange(event) {
        if (typeof onInputChange === "function") {
          onInputChange(event);
        }
      }
    });
  }, [getDropdownProps, handleKeyDown, onInputChange]);
  const onClear = React.useCallback(() => {
    setSelectedItems([]);
    if (typeof onChangeProp === "function") {
      onChangeProp([]);
    }
  }, [setSelectedItems, onChangeProp]);
  return /*#__PURE__*/React.createElement(BaseComboBox, _extends({
    value: selectedItems,
    onChange: onChange,
    onClear: onClear,
    onDelete: handleDelete,
    onInputChange: onInputChange,
    renderOption: renderOption,
    getDropdownProps: handleGetDropdownProps,
    getOptionLabel: getOptionLabel,
    freeSolo: freeSolo,
    filterOptions: handleFilterOptions,
    size: size,
    disabled: disabled,
    required: required
  }, rest));
}